import React, { useState } from 'react';
import { Button } from 'src/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from 'src/components/ui/dialog';
import { clsx } from 'clsx';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { booleanScheme, stringEmailScheme, stringScheme } from 'src/utils/validation-schemes';
import { WorkflowNameAndDescriptionBlock } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowPopupBock/WorkflowNameAndDescriptionBlock';
import { WorkflowTriggersBlock } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowPopupBock/WorkflowTriggersBlock';
import { WorkflowActionsBlock } from 'src/pages/ConfigurationPage/WorkflowsTab/WorkflowPopupBock/WorkflowActionsBlock';
import { getWorkflowRequestAction } from 'src/pages/ConfigurationPage/WorkflowsTab/getWorkflowRequestAction';
import { validateWorkflowActions } from 'src/helpers/validateWorkflowActions';

const AddIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path
      opacity="0.8"
      d="M6.2 6.2V1H7.8V6.2H13V7.8H7.8V13H6.2V7.8H1V6.2H6.2Z"
      fill="#05050D"
      stroke="#05050D"
      strokeWidth="0.5"
    />
  </svg>
);

export const AddWorkflowPopup = ({
  disabled = false,
  trigger = null,
  onEditClick = () => {},
  onSubmit = (_workflow?: any) => {},
}: {
  disabled?: boolean;
  trigger?: any;
  onEditClick?: () => void;
  onSubmit?: (workflow: any) => void;
}) => {
  const [open, setOpen] = useState(false);

  const {
    handleSubmit,
    control,
    reset,
    watch,
    trigger: formTrigger,
    setError,
  } = useForm({
    defaultValues: {
      action: [
        {
          action_type: 'clarify',
          action_fields_custom: false,
          action_message_type: 'transcript',
          action_message: '',
          action_escalated_list: '',
          action_data_collection: '',
          message_before: '',
          message_after: '',
          message_close: '',
        },
      ],
      trigger_type: 'when_asked',
      trigger_value: '',
      name: '',
      description: '',
    },
    resolver: yupResolver(
      yup.object({
        name: stringScheme({ required: true }),
        description: stringScheme({ required: false }),
        trigger_type: stringScheme({ required: false }),
        trigger_value: stringScheme({ required: true }),
        action: yup.array(
          yup.object({
            action_type: stringScheme({ required: false }),
            action_message: stringScheme({ required: false }),
            action_escalated_list: stringScheme({ required: false }),
            action_data_collection: stringScheme({ required: false }),
            action_fields_name: booleanScheme({ required: false }),
            action_fields_email: booleanScheme({ required: false }),
            action_fields_phone_number: booleanScheme({ required: false }),
            action_fields_custom: booleanScheme({ required: false }),
            action_message_type: stringScheme({ required: false }),
            message_before: stringScheme({ required: false }),
            receiver_email_addresses: stringEmailScheme({ required: false, format: true }),
            message_after: stringScheme({ required: false }),
            message_close: stringScheme({ required: false }),
            action_fields_custom_array: yup.array(
              yup.object({
                name: stringScheme({ required: false }),
                description: stringScheme({ required: false }),
              }),
            ),
          }),
        ),
      }),
    ),
  });

  const submit = (data) => {
    const isValid = validateWorkflowActions(data, setError);

    if (!isValid) {
      return;
    }

    const action = data.action.map((action) => getWorkflowRequestAction(action));

    const workflow = {
      enabled: true,
      type: 'custom',
      name: data.name,
      description: data.description,
      trigger_type: data.trigger_type,
      trigger_value: data.trigger_value,
      action,
    };

    onSubmit(workflow);
    close();
  };

  const close = () => {
    reset();
    setOpen(false);
  };

  const onOpenChange = (isOpen) => {
    if (!isOpen) {
      reset();
    }

    setOpen(isOpen);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogTrigger
          disabled={disabled}
          asChild
          className={clsx(disabled && 'pointer-events-none opacity-50', 'cursor-pointer')}
          onClick={onEditClick}
        >
          {Boolean(trigger) ? (
            trigger
          ) : (
            <Button
              variant={'outline'}
              className="flex flex-row items-center gap-2 text-ebony_opacity_80 font-medium"
            >
              <span>Add Workflow</span>
              <span>
                <AddIcon />
              </span>
            </Button>
          )}
        </DialogTrigger>
        <DialogContent closeButtonHidden className={'overflow-hidden'}>
          <DialogHeader>
            <DialogTitle>Custom workflow settings</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 w-full">
            <div className="content w-full">
              <form
                id="add-custom-workflow-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSubmit(submit)(e);
                }}
                className={'max-h-[60vh] w-full max-w-[600px] overflow-auto pr-4'}
              >
                <div className={'flex flex-col h-full w-full gap-5'}>
                  <WorkflowNameAndDescriptionBlock control={control} />
                  <WorkflowTriggersBlock control={control} />
                  <WorkflowActionsBlock control={control} watch={watch} trigger={formTrigger} />
                </div>
              </form>
              <div className="flex justify-center mt-5">
                <div className="flex gap-2.5">
                  <Button variant="outline" type="button" className="min-w-24" onClick={close}>
                    Cancel
                  </Button>
                  <Button
                    form="add-custom-workflow-form"
                    // disabled={!(isDirty && isValid)}
                    className="min-w-24"
                  >
                    Add
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
